<template>
  <div class="app-cpost">
    <div
      v-show="!addressFallback"
    >
      <AppInput
        v-show="!loading"
        ref="control"
        label="My Home Address"
        id="street_address"
        name="street_address"
        :class="unverifiedAddress ? 'app-cpost__input__unverified' : 'app-cpost__input'"
        :error="error"
        :value="streetAddress"
        v-data-layer
        :data-layer="dataLayerField"
        :icon="!!addressValue.verified"
        autocomplete="off"
        @input="updateStreetAddress($event);"
      >
        <FontAwesomeIcon
          v-if="addressValue.verified"
          slot="icon"
          icon="check-circle"
          class="verified-icon"
        />
        <small
          v-if="unverifiedAddress"
          slot="error"
          class="app-cpost__input__unverified__error"
        >
          We could not find your address, please correct it or <u @click="showFallback">type it in manually</u>.
        </small>
      </AppInput>
      <!-- Loading Spinner -->
      <div
        v-if="loading"
        class="loading-icon"
      >
        <FontAwesomeIcon
          class="loading-icon__svg"
          icon="sync"
        />
      </div>

      <!-- Canada Post Results Frame -->
      <AppCanadaPostResult
        :address="addressValue"
      />
    </div>

    <AppCanadaPostFallback
      v-if="addressFallback"
      :data-layer-street="dataLayerStreet"
      :data-layer-city="dataLayerCity"
      :data-layer-province="dataLayerProvince"
      :data-layer-postal="dataLayerPostal"
      @fallback-validated="$emit('fallback-validated')"
    />
  </div>
</template>

<script>
import AppCanadaPostFallback from './AppCanadaPostFallback'
import AppCanadaPostResult from '@/components/common/canada-post/AppCanadaPostResult'
import AppInput from '../inputs/AppInput'
// import AppSelect from '../inputs/AppSelect'
import Axios from 'axios'
import cpostFields from '@/components/common/canada-post/cpostFields'
import datalayer from '@/data-layer'
/* eslint-disable-next-line */
import(/* webpackChunkName: "addresscomplete" */ '@/assets/js/addresscomplete-2.30.min.js')

export default {
  // Name
  name: 'app-canada-post',

  // Components
  components: {
    AppCanadaPostFallback,
    AppCanadaPostResult,
    AppInput
    // AppSelect
  },

  // Props
  props: {
    error: String,
    validateFallback: Boolean,
    dataLayerField: {
      type: String,
      required: false,
      default: 'street_address_field'
    },
    dataLayerSelected: {
      type: String,
      required: false,
      default: 'address_selected'
    },
    dataLayerManual: {
      type: String,
      required: false,
      default: 'address_manual'
    },
    dataLayerStreet: {
      type: String,
      required: false,
      default: 'street_field'
    },
    dataLayerCity: {
      type: String,
      required: false,
      default: 'city_field'
    },
    dataLayerProvince: {
      type: String,
      required: false,
      default: 'province_field'
    },
    dataLayerPostal: {
      type: String,
      required: false,
      default: 'postal_code_field'
    },
    unverifiedAddress: {
      type: Boolean,
      default: false
    }
  },

  // Data
  data () {
    return {
      control: undefined,
      streetAddress: undefined,
      loading: false,
      cpostOffline: false,
      cpostKey: process.env.VUE_APP_CPOST_KEY,
      formFields: [{
        element: 'street_address',
        field: 'Line1'
      }]
    }
  },

  // Computed
  computed: {
    addressValue () {
      return {
        verified: this.$store.state.application.c_post_verified,
        line1: this.$store.state.application.c_post_Line1,
        line2: this.$store.state.application.c_post_Line2,
        city: this.$store.state.application.c_post_City,
        province: this.$store.state.application.c_post_Province,
        postalCode: this.$store.state.application.c_post_PostalCode,
        fullAddress: this.$store.state.application.c_post_fullAddress
      }
    },

    addressFallback () {
      return this.$store.state.addressFallback
    }
  },

  // Mounted
  mounted () {
    this.canadaPostInit() // Initialize cpost

    if (this.addressValue.fullAddress) {
      this.streetAddress = this.addressValue.line1
    }

    // Unhide the pca if this step isn't using the address fallback
    const pcaElement = document.querySelector('.pca')
    if (!this.addressFallback && pcaElement) {
      document.querySelector('.pca').classList.remove('hide')
    }
  },

  // Before Destroy
  beforeDestroy () {
    if (window.control) window.control.destroy()
    document.querySelector('.pcatext.pcanotification').remove()
  },

  // Methods
  methods: {
    /**
     * Canada Post Init
     * @desc Initialize the canada post auto complete input 'control'
     */
    async canadaPostInit () {
      try {
        if (!window.control) {
          await this.sendTestRequest()
        }
        addressComplete.destroy()
      } catch (error) {
        console.warn('error with cpost api')
      }
      // Canada Post API settings
      const options = {
        key: this.cpostKey,
        countries: {
          value: 'CAN'
        }
      }

      // Autocomplete Input Control
      if (pca) window.control = new pca.Address(this.formFields, options)

      // 'populate' event fires when the user selects an address
      window.control.listen('populate', (address) => {
        this.populateAddress(address)
      })

      // 'error' event fires if addressComplete encouters an error
      window.control.listen('error', (error) => {
        console.warn(error)
        this.$store.updateApplication('c_post_error', 1)
        this.showFallback(error)
      })
    },

    /**
     * Send Test Request
     * @desc tests the Cpost to see if it's working
     * If it returns an error, show the fallback
     */
    async sendTestRequest () {
      try {
        this.loading = true
        const response = await Axios.get(
          '//ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3ex.ws?Key=' + this.cpostKey + '&Country=CAN&SearchTerm=a&LanguagePreference=en',
          { timeout: process.env.VUE_APP_CPOST_TIMEOUT || 5000 }
        )
        if (response.data.Items[0].Error) {
          throw new Error()
        }
      } catch (error) {
        this.cpostOffline = true
        this.$store.updateApplication('c_post_error', 1)
        this.showFallback(error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Populate the address fields
     * @emits populate
     */
    populateAddress (address) {
      address.verified = 1
      address.error = 0

      cpostFields.forEach(item => {
        this.$store.updateApplication(item.field, address[item.key])
      })

      this.streetAddress = address.FormattedLine1
      this.$store.updateApplication('street', '')
      this.$emit('populate')
      datalayer.pushEvent('event', this.dataLayerSelected)
    },

    /**
     * Update Street Address
     * Resets 'address.verified' to false
     * @emits input
     */
    updateStreetAddress (value) {
      this.$emit('input')
      this.streetAddress = value
      this.$store.updateApplication('c_post_verified', 0)
      this.$nextTick(() => {
        if (!window.control) return
        window.control.reposition()
        this.showFallbackPlaceholder()
      })
    },

    /**
     * Show Fallback Placeholder Text
     */
    showFallbackPlaceholder () {
      // Grab the list of canada post footer elements (there are 2)
      const pcafooters = document.querySelectorAll('.pcafooter')
      const pcafooter = pcafooters[0]

      // If address length > 1, Add fallback text class .show-fallback-text
      if (!this.streetAddress) {
        return
      }
      if (this.streetAddress.length > 1) {
        this.$nextTick(() => {
          pcafooter.classList.remove('no-click')
          pcafooter.classList.add('show-fallback-text')

          // Add a click listener to the .pcafooter
          this.fallbackListener = pcafooter.addEventListener('click', () => {
            // Show the fallback fields
            this.showFallback('manual address fallback')
            document.querySelector('.pca').classList.add('hide')
          }, false)
        })
      } else {
        // Remove the .show-fallback-text class and click listener
        pcafooter.classList.remove('show-fallback-text')
        pcafooter.classList.add('no-click')
        pcafooter.removeEventListener('click', this.fallbackListener)
        this.fallbackListener = undefined
      }
    },

    /**
     * Show Address Fallback
     */
    showFallback (error) {
      this.$store.enableAddressFallback()
      cpostFields.forEach(item => {
        this.$store.updateApplication(item.field, '')
      })
      datalayer.pushEvent('event', this.dataLayerManual)
      if (error) {
        this.$store.updateApplication('c_post_error_description', error)
      }
    }
  }
}
</script>

<style lang="scss">
.app-cpost__input__unverified {
  .app-input__error {
    max-width: 300px;
    padding-top: 10px;
    position: relative;
    bottom: 0;
    line-height: 15px;
    @include breakpoint('tablet-portrait-up') {
      max-width: 100%;
      padding-top: 0;
      top: 10px;
    }
    @include breakpoint('tablet-landscape-up') {
      max-width: 320px;
    }
    @include breakpoint('desktop-up') {
      max-width: 100%;
    }
  }
  &__error {
    u {
      font-weight: 600;
      cursor: pointer;
    }
  }
}
</style>

<style scoped lang="scss">
.app-cpost {
  position: relative;
  width: 100%;

  &__input {
    z-index: 5;

    :deep() .app-input__field.has-icon {
      padding-left: 40px;

      @include breakpoint('tablet-portrait-up') {
        padding-left: 55px;
      }
    }
  }

  .verified-icon {
    color: #00C700;
    position: relative;
    // left: -5px;
    font-size: 16px;
  }
}

.app-cpost-result {
  top: -5px;
  @include breakpoint('tablet-portrait-up') {
    top: -25px;
  }
}

.loading-icon {
  display: block;
  text-align: center;
  margin: auto;
  font-size: 40px;
  margin: 25px 0 17px 0;

  &__svg {
    margin: 0;
    display: inline-block;
    width: 40px;
    height: 40px;
    transform-origin: 50% 50%;
    animation: loading 1000ms ease-out;
    animation-iteration-count: infinite;
  }
}

@keyframes loading {
  0% {
    transform: rotate(180deg);
  }

  100% {
    transform: rotate(360deg)
  }
}
</style>
